import React, { useMemo, useState } from "react";
import { map, lowerCase, toUpper } from "lodash";
import styled from "styled-components";
import {
  colors,
  ConnectedPlusIcon,
  EllipsisIcon,
  fontFamilies,
  IconButton,
} from "@commonsku/styles";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { getDefaultBillFromPodCheckout } from "../../../../redux/bills";
import { Bill } from "../../../../types/bills";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { ClientColumn, SupplierColumn } from "../common";
import DropdownMenu, { MenuTrigger } from "../../../DropdownMenu";
import { generateUUID, getAbsoluteUrl } from "../../../../utils";
import { useHasCapabilities } from "../../../../hooks";
import BillPopup from "../../../bills/BillPopup";
import { usePodBillsRefresh } from "../../../finance/PodBillsRefreshContext";

const CreatedColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Date> = {
  header: "Created",
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_invoiced"],
  accessorKey: "date_invoiced",
  canSort: true,
  orderByField: "date_invoiced",
  width: 110,
};

const ShopColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: "Shop",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["shop_number", "shop_name"],
  accessorKey: "shop_number",
  canSort: true,
  orderByField: "shop_name",
  width: 200,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ shop_number, shop_name }) => ({
    href: shop_number ? `/shop/${shop_number}` : null,
    text: shop_number ? `#${shop_number} - ${shop_name}` : shop_name,
  }),
};

const ReferenceColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: "Reference",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["customer_po", "order_id"],
  accessorKey: "customer_po",
  canSort: true,
  orderByField: "customer_po",
  width: 140,
  dataTransform: ({ customer_po, order_id }) => ({
    href: order_id
      ? getAbsoluteUrl(`public_order.php?order_id=${order_id}`)
      : null,
    text: customer_po,
  }),
};

const ValueColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Money> = {
  header: "Value",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["total", "currency_id"],
  accessorKey: "total",
  canSort: true,
  orderByField: "total",
  width: 110,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ total, currency_id }) => ({
    amount: total,
    currency_id,
  }),
};

const StyledDropdownMenu = styled(DropdownMenu)`
  & > .dropdown-menu-trigger {
    color: ${colors.primary1.main};
    border-radius: 5px;
    padding: 4px 4px 0 4px;
  }
  &.dropdown-menu-open > .dropdown-menu-trigger {
    border: 2px solid ${colors.primary1.main};
  }
  &&& > ul {
    top: -0.5rem;
    right: 3rem;
    border: 2px solid ${colors.primary1.main};
    border-radius: 5px;
    margin-top: 4px;
    min-width: 200px;

    & > li {
      font-family: ${fontFamilies.regular};
      color: ${colors.neutrals.bodyText};
    }
  }
`;

// Confirm Bill is laid out but not wired for MVP.
function PodBillActions({ row }: { row: Record<string, unknown> }) {
  const hasCreateBill = useHasCapabilities(["CREATE-BILL"]);
  const refreshBills = usePodBillsRefresh();
  const [showCreateBill, setShowCreateBill] = useState(false);
  const [bill, setBill] = useState<Bill | undefined>();

  const handleReviewBill = async () => {
    const bill = await getDefaultBillFromPodCheckout(String(row.order_id));
    bill.number = String(row.pod_invoice_id);
    bill.podInvoiceOrderId = String(row.pod_invoice_order_id);
    const items = map(
      ["pick_and_pack", "item_cost", "shipping"],
      (field) => {
        return {
          refId: generateUUID(),
          code: toUpper(field),
          description: lowerCase(field),
          quantity: 1,
          unitCost: Number(row[field]),
          excludeFromMargin: false,
          taxes: [],
        };
      },
    );
    bill.orders[0].items.push(...items);
    setBill(bill);
    setShowCreateBill(true);
  };

  const handleClose = (saved: boolean) => {
    setShowCreateBill(false);
    if (saved) {
      refreshBills();
    }
  };

  const options = useMemo(
    () => [
      {
        value: (
          <IconButton
            variant="text"
            size="small"
            iconPosition="left"
            Icon={ConnectedPlusIcon}
            onClick={handleReviewBill}
            style={{ width: "calc(100% - 13px)", margin: "4px 6.5px" }}
          >
            Review Bill
          </IconButton>
        ),
        hideOnClick: true,
        onClick: () => {},
      },
    ],
    [],
  );

  return !hasCreateBill ? null : (
    <>
      {showCreateBill && bill && (
        <BillPopup bill={bill} onClose={handleClose} />
      )}
      <StyledDropdownMenu options={options} align="right">
        <MenuTrigger>
          <EllipsisIcon altText="Actions" />
        </MenuTrigger>
      </StyledDropdownMenu>
    </>
  );
}

const ActionColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Generic> = {
  header: "Actions",
  type: BASE_COLUMN_TYPES.Generic,
  fields: [
    "pod_invoice_order_id",
    "pod_invoice_id",
    "order_id",
    "pick_and_pack",
    "item_cost",
    "shipping",
    "total",
    "customer_po",
  ],
  accessorKey: "actions",
  canSort: false,
  width: 90,
  justify: "center",
  dataTransform: (row) => ({
    cell: <PodBillActions row={row} />,
  }),
};

// The mockup's Description column is absent: a bill line has no description of its own, and the
// report is already one row per invoice line.
export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  CreatedColumn,
  ShopColumn,
  { ...SupplierColumn, width: 200 },
  ReferenceColumn,
  { ...ClientColumn, width: 220 },
  ValueColumn,
  ActionColumn,
];
